import { useState, useEffect, useCallback } from 'react';
import toastService from '../../../services/toastService';
import authService from '../../../services/authService';

/**
 * Custom hook for managing scan data and table operations
 */
export const useScanData = (pentestServer, showConfirm = null) => {
  const [tableData, setTableData] = useState([]);
  const [statsData, setStatsData] = useState({
    total: 0,
    completed: 0,
    info: [],
    chartType: 'bar',
    chartData: [{ data: [] }],
    chartHeight: 30
  });
  const [selectedScans, setSelectedScans] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  //TEST - delete it
  useEffect(() => {
    console.log("tableData changed: ", tableData);
  }, [tableData]);

  useEffect(() => {
    console.log("useScanData MOUNTED");
    return () => {
      console.log("useScanData UNMOUNTED");
    };
  }, []);

  const ensureJson = async (response) => {
    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Unexpected response (expected JSON). First 200 chars: ${text.slice(0, 200)}`);
    }
    return response.json();
  };

  // Function to refresh table data
  const refreshTableData = useCallback(async () => {
    try {
      console.log("useScanData: refreshTableData called at", new Date().toISOString());
      const response = await authService.authenticatedCall(`${pentestServer}/scans`);
      if (!response.ok) {
        // Try to extract JSON error if possible
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const data = await ensureJson(response);
          msg = data?.error || data?.message || msg;
        } catch (e) {
          msg = `${msg}`;
        }
        throw new Error(msg);
      }
      const data = await ensureJson(response);
      console.log("useScanData: received data:", Array.isArray(data) ? data.length : 'n/a', "scans");
      setTableData(Array.isArray(data) ? data : []);

      // Update stats
      const totalScans = Array.isArray(data) ? data.length : 0;
      const completedScans = Array.isArray(data) ? data.filter(scan => scan.status === 'completed').length : 0;
      console.log("useScanData: stats - total:", totalScans, "completed:", completedScans);
      setStatsData(prevStatsData => ({
        ...prevStatsData,
        total: totalScans,
        completed: completedScans,
        info: [
          {
            icon: "fa fa-check text-success",
            text: `${completedScans} / ${totalScans} completed`
          },
        ],
        chartData: [{ data: [completedScans, totalScans - completedScans] }],
        chartType: 'bar',
        //chartHeight: 30
      }));
    } catch (error) {
      console.error("Could not refresh table data:", error);
      toastService.error(`Could not refresh table data: ${error.message}`);
    }
  }, [pentestServer]);

  // Function to clear table data (display only)
  const clearTableData = useCallback(() => {
    console.log("useScanData: clearTableData called");
    setTableData([]);
    setSelectedScans(new Set());
    setStatsData({
      total: 0,
      completed: 0,
      info: [],
      chartType: 'bar',
      chartData: [{ data: [] }],
      chartHeight: 30
    });
  }, []);

  // Function to handle checkbox selection
  const handleScanSelection = useCallback((scanId, isSelected) => {
    const newSelectedScans = new Set(selectedScans);
    if (isSelected) {
      newSelectedScans.add(scanId);
    } else {
      newSelectedScans.delete(scanId);
    }
    setSelectedScans(newSelectedScans);
  }, [selectedScans]);

  // Function to handle select all checkbox
  const handleSelectAll = useCallback((isSelected) => {
    if (isSelected) {
      const allScanIds = new Set(tableData.map(scan => scan.id));
      setSelectedScans(allScanIds);
    } else {
      setSelectedScans(new Set());
    }
  }, [tableData]);

  // Function to delete selected scans
  const handleDeleteSelected = useCallback(async () => {
    if (selectedScans.size === 0) {
      toastService.warning('Please select scans to delete.');
      return;
    }

    const confirmed = showConfirm 
      ? await showConfirm({
          title: 'Delete Selected Scans',
          message: `Are you sure you want to delete ${selectedScans.size} selected scan(s)? This action cannot be undone.`,
          confirmText: 'Delete Scans',
          confirmVariant: 'danger'
        })
      : window.confirm(`Are you sure you want to delete ${selectedScans.size} selected scan(s)? This action cannot be undone.`);

    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      const scanIdsArray = Array.from(selectedScans);
      const response = await authService.authenticatedCall(`${pentestServer}/scans`, {
        method: 'DELETE',
        body: JSON.stringify({ scanIds: scanIdsArray })
      });

      if (!response.ok) {
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await ensureJson(response);
          msg = errorData?.error || errorData?.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      const result = await ensureJson(response);
      toastService.success(`Successfully deleted ${result.deletedRows} scan(s).`);
      
      await refreshTableData();
      setSelectedScans(new Set());
    } catch (error) {
      console.error('Error deleting scans:', error);
      toastService.danger(`Error deleting scans: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedScans, pentestServer, refreshTableData, showConfirm]);

  // Function to delete individual scan
  const handleDeleteScan = useCallback(async (scanId) => {
    const confirmed = showConfirm 
      ? await showConfirm({
          title: 'Delete Scan',
          message: 'Are you sure you want to delete this scan? This action cannot be undone.',
          confirmText: 'Delete Scan',
          confirmVariant: 'danger'
        })
      : window.confirm('Are you sure you want to delete this scan? This action cannot be undone.');

    if (!confirmed) {
      return;
    }

    try {
      const response = await authService.authenticatedCall(`${pentestServer}/scan/${scanId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await ensureJson(response);
          msg = errorData?.error || errorData?.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      await ensureJson(response);
      toastService.success('Scan deleted successfully.');
      
      await refreshTableData();
    } catch (error) {
      console.error('Error deleting scan:', error);
      toastService.danger(`Error deleting scan: ${error.message}`);
    }
  }, [pentestServer, refreshTableData, showConfirm]);

  // Initial data fetch
  /* useEffect(() => {
    refreshTableData();
  }, [refreshTableData]); */

  return {
    tableData,
    statsData,
    selectedScans,
    isDeleting,
    refreshTableData,
    clearTableData,
    handleScanSelection,
    handleSelectAll,
    handleDeleteSelected,
    handleDeleteScan
  };
};